import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Users,
  Edit,
  Trash2,
  MoreVertical,
  Upload,
  X,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const AgentManagement = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", bio: "", license_number: "",
    specializations: "", experience_years: "", rating: "", total_sales: ""
  });

  useEffect(() => {
    if (user?.user_id) {
      fetchAgents();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`, { withCredentials: true });
      setAgents(response.data || []);
    } catch (error) {
      console.error("Error fetching agents:", error);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error("Photo is too large (max 10MB)");
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      setUploadedPhoto(response.data);
      toast.success("Photo uploaded!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to upload photo: ${errorMsg}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name || "",
      email: agent.email || "",
      phone: agent.phone || "",
      bio: agent.bio || "",
      license_number: agent.license_number || "",
      specializations: agent.specializations?.join(", ") || "",
      experience_years: agent.experience_years?.toString() || "",
      rating: agent.rating?.toString() || "",
      total_sales: agent.total_sales?.toString() || ""
    });
    setUploadedPhoto(agent.photo ? { url: agent.photo } : null);
    setEditDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/agents/${editingAgent.agent_id}`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        bio: formData.bio || null,
        photo: uploadedPhoto?.url || null,
        license_number: formData.license_number || null,
        specializations: formData.specializations ? formData.specializations.split(",").map(s => s.trim()) : [],
        experience_years: parseInt(formData.experience_years) || 0,
        rating: parseFloat(formData.rating) || 0,
        total_sales: parseInt(formData.total_sales) || 0
      }, { withCredentials: true });

      toast.success("Agent updated!");
      setEditDialogOpen(false);
      fetchAgents();
    } catch (error) {
      toast.error("Failed to update agent");
    }
  };

  const handleDelete = async (agentId) => {
    if (!confirm("Are you sure you want to remove this agent?")) return;

    try {
      await axios.delete(`${API}/agents/${agentId}`, { withCredentials: true });
      toast.success("Agent removed");
      fetchAgents();
    } catch (error) {
      toast.error("Failed to remove agent");
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-serif">Manage Agents</h1>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="bg-card rounded-sm p-4 animate-pulse">
              <div className="h-6 bg-muted rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-muted rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : agents.length === 0 ? (
        <div className="text-center py-16 bg-muted rounded-sm">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-serif text-xl mb-2">No Agents</h3>
          <p className="text-muted-foreground">No agents registered yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {agents.map((agent) => (
            <div key={agent.agent_id} className="bg-card border border-border rounded-sm p-4 flex gap-4">
              <img
                src={agent.photo || "https://ui-avatars.com/api/?name=" + encodeURIComponent(agent.name)}
                alt={agent.name}
                className="w-20 h-20 rounded-full object-cover"
              />
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-lg">{agent.name}</h3>
                    <p className="text-sm text-muted-foreground">{agent.email}</p>
                    <p className="text-sm text-muted-foreground">{agent.phone}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>&#11088; {agent.rating || 0}</span>
                      <span>&#8226;</span>
                      <span>{agent.total_sales || 0} sales</span>
                      <span>&#8226;</span>
                      <span>{agent.experience_years || 0} years exp</span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(agent)}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(agent.agent_id)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Agent Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Agent</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Profile Photo</label>
              <div className="flex items-center gap-4">
                {uploadedPhoto ? (
                  <div className="relative">
                    <img 
                      src={uploadedPhoto.url} 
                      alt="Profile" 
                      className="w-20 h-20 rounded-full object-cover border-2 border-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setUploadedPhoto(null)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                    <Users className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" id="edit-agent-photo" />
                  <label htmlFor="edit-agent-photo" className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-muted hover:bg-muted/80 rounded-sm text-sm">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Uploading..." : "Upload"}
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <Input required type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">License</label>
                <Input value={formData.license_number} onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Experience (years)</label>
                <Input type="number" value={formData.experience_years} onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rating</label>
                <Input type="number" step="0.1" max="5" value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Total Sales</label>
                <Input type="number" value={formData.total_sales} onChange={(e) => setFormData({ ...formData, total_sales: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Specializations (comma-separated)</label>
              <Input value={formData.specializations} onChange={(e) => setFormData({ ...formData, specializations: e.target.value })} placeholder="Residential, Land, Commercial" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bio</label>
              <Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} rows={3} />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1">Save Changes</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentManagement;
